/**
 * `handleSaveChange` and `handleFinalizeChange` — the two side-effecting
 * entry points that PATCH and finalize the report via RTK Query. Split out
 * from `useReportSavePipeline` so the derivation logic and the mutation
 * logic each stay under the hook size budget.
 */

import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { notification } from 'antd';
import { ReportUpdatePayload, Report as MReport } from '@/models/report';
import { OptionAPI } from '@/state/slices/options';
import { parseReportSaveError } from '../utils/parseReportSaveError';

type NotificationApi = ReturnType<typeof notification.useNotification>[0];

type UnwrappableMutation<T> = (arg: T) => { unwrap: () => Promise<unknown> };

interface UseReportPersistenceArgs {
  currentReport: MReport | null;
  resolvePayloadForSave: () => ReportUpdatePayload | null;
  refreshEditBaselineFromEditor: () => void;
  updateReport: UnwrappableMutation<ReportUpdatePayload>;
  finalizeReport: UnwrappableMutation<string>;
  logSave: (uuid: string) => void;
  notification: NotificationApi;
  hasOpenAddendum: boolean;
  addendumOpenedByEmail: string | undefined;
  addendumOpenedByName: string;
  activeUserEmail: string;
}

export function useReportPersistence({
  currentReport,
  resolvePayloadForSave,
  refreshEditBaselineFromEditor,
  updateReport,
  finalizeReport,
  logSave,
  notification: api,
  hasOpenAddendum,
  addendumOpenedByEmail,
  addendumOpenedByName,
  activeUserEmail,
}: UseReportPersistenceArgs) {
  const dispatch = useDispatch();

  // Free-form tags created on a worksheet save expand the tag-class catalog.
  // Invalidate so the worklist filter dropdown sees the new value without
  // needing a page reload. Skip when the array is empty — an empty payload
  // only removes tags from this report, never expands the global catalog.
  const invalidateTagClassesIfTagged = useCallback(
    (payload: ReportUpdatePayload) => {
      if (payload.tags && payload.tags.length > 0) {
        dispatch(
          OptionAPI.util.invalidateTags([{ type: 'TagClasses', id: 'LIST' }])
        );
      }
    },
    [dispatch]
  );

  const handleFinalizeChange = useCallback(async () => {
    if (hasOpenAddendum && addendumOpenedByEmail !== activeUserEmail) {
      api.warning({
        message: 'Addendum in progress',
        description: `This report has an open addendum by ${addendumOpenedByName}`,
        duration: 5,
      });
      return;
    }
    if (!currentReport?.uuid) {
      api.warning({
        message: 'Invalid Report',
        description: 'Please check the selected report',
      });
      return;
    }
    const updatePayload = resolvePayloadForSave();
    if (!updatePayload) return;
    try {
      const updated = await updateReport(updatePayload).unwrap();
      // Tags are saved by updateReport — invalidate before finalize so the
      // catalog stays in sync even if finalize throws.
      invalidateTagClassesIfTagged(updatePayload);
      const finalizeUuid = (updated as any)?.uuid || currentReport.uuid;
      await finalizeReport(finalizeUuid).unwrap();
      refreshEditBaselineFromEditor();
    } catch (err) {
      api.error({
        message: 'Error finalizing report',
        description: (err as any)?.data?.detail || String(err),
      });
    }
  }, [
    hasOpenAddendum,
    addendumOpenedByEmail,
    addendumOpenedByName,
    activeUserEmail,
    currentReport,
    resolvePayloadForSave,
    updateReport,
    finalizeReport,
    refreshEditBaselineFromEditor,
    invalidateTagClassesIfTagged,
    api,
  ]);

  // Autosave repeats every few seconds — notify once per distinct error.
  const lastSaveErrorRef = useRef<string | null>(null);

  // Resolves `true` when there is nothing to save or the PATCH succeeded, and
  // `false` when it failed. Errors are still surfaced as a toast here — the
  // boolean exists so callers that navigate away (e.g. "Preview Report") can
  // flush pending edits first and stay put if the flush did not land.
  const handleSaveChange = useCallback((): Promise<boolean> => {
    if (!currentReport?.uuid) return Promise.resolve(true);
    const payload = resolvePayloadForSave();
    if (!payload) return Promise.resolve(true);
    return updateReport(payload)
      .unwrap()
      .then(() => {
        lastSaveErrorRef.current = null;
        invalidateTagClassesIfTagged(payload);
        refreshEditBaselineFromEditor();
        logSave(currentReport.uuid);
        return true;
      })
      .catch((err: { data?: unknown }) => {
        const description = parseReportSaveError(
          err?.data,
          payload,
          currentReport.measurements
        );
        if (lastSaveErrorRef.current !== description) {
          lastSaveErrorRef.current = description;
          api.error({ message: 'Report save failed', description });
        }
        return false;
      });
  }, [
    currentReport,
    resolvePayloadForSave,
    updateReport,
    invalidateTagClassesIfTagged,
    refreshEditBaselineFromEditor,
    logSave,
    api,
  ]);

  return { handleSaveChange, handleFinalizeChange };
}
